import { ArrowLeft20, Close20, Search16 } from '@carbon/icons-react';
import { isNaN } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Space, SpaceTab } from 'api/space';
import { RoutePaths } from 'constant';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { SearchInput } from '../../components/cp-input';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import { ListOrGridMode } from '../all-files/types';
import { BackIconWrapper } from '../chat/components';
import { CpSectionWrapper } from '../common';
import { CpWidgetHeader } from '../common/header';
import { StyledBackWrapperInCpSpaces } from '../space/cp-space';
import { CpAccountSpacesForLaptop } from './cp-account-spaces.laptop';
import { CpAccountSpacesForMobile } from './cp-account-spaces.mobile';
import { CpAccountSpacesData } from './types';

export const CpAccountSpaces: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  projectId,
  disabled,
}) => {
  const router = useHistory();
  const isResponsive = useIsTablet();
  const background = useGetBackground(widget);
  const data = useMemo(() => _data as CpAccountSpacesData, [_data]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<ListOrGridMode>(ListOrGridMode.Grid);
  const { hashMap, setHashMap, removeKey } = useCustomerPortalHashMap();
  const translations = useTranslate();

  const _currentSpace: Space | undefined = useMemo(() => {
    const split = hashMap?.acc_spaces?.split('.');
    const isCurrentProjectANumber = !isNaN(Number(split?.[0]));
    const isCurrentSpaceANumber = !isNaN(Number(split?.[1]));

    if (isCurrentSpaceANumber && isCurrentProjectANumber) {
      const spaceProjectId = Number(split?.[0]);
      const spaceId = Number(split?.[1]);

      const project = data?.spaces.find(
        (space) => space.project.projectId === spaceProjectId,
      );

      const space = project?.spaces?.find((s) => s.spaceId === spaceId);

      if (space) {
        return {
          ...space,
          spaceName: search?.trim()
            ? translations('search_results')
            : space?.spaceName,
        };
      }

      return space;
    }

    if (isResponsive) {
      return undefined;
    }

    return data?.spaces?.at(0)?.spaces?.at(0);
  }, [data?.spaces, hashMap?.acc_spaces, isResponsive, search, translations]);

  const currentSpace = useMemo(() => {
    if (search?.trim()) {
      return _currentSpace
        ? { ..._currentSpace, spaceName: translations('search_results') }
        : _currentSpace;
    }

    return _currentSpace;
  }, [_currentSpace, search, translations]);

  const allSpaceTabs = useMemo(() => {
    return (
      data?.spaces
        ?.flatMap((space) => space?.spaces)
        ?.flatMap((e) => e?.spaceTabs) ?? []
    );
  }, [data?.spaces]);

  const currentSpaceTabs = useMemo(() => {
    if (search?.trim()) {
      return (
        allSpaceTabs?.filter((e) =>
          e?.spaceTabTitle
            ?.toLowerCase?.()
            ?.includes(search?.trim?.().toLowerCase?.()),
        ) ?? []
      );
    }

    const spaceTabs = currentSpace?.spaceTabs;

    return (
      spaceTabs?.filter((e) =>
        e?.spaceTabTitle
          ?.toLowerCase?.()
          ?.includes(search?.trim?.().toLowerCase?.()),
      ) ?? []
    );
  }, [allSpaceTabs, currentSpace?.spaceTabs, search]);

  const onSpaceClick = useCallback(
    (
      args:
        | {
            projectId: number;
            spaceId: number;
          }
        | undefined,
    ) => {
      const spaceId = args?.spaceId;
      const _projectId = args?.projectId;

      if (!spaceId) {
        removeKey('acc_spaces');
        setSearch('');

        return;
      }
      setHashMap('acc_spaces', `${_projectId}.${spaceId}`);
    },
    [removeKey, setHashMap],
  );

  const onSpaceTabClick = useCallback(
    (spaceTab: SpaceTab) => {
      router.push({
        pathname: RoutePaths.customerPortalSpaceTabById
          .replace(':spaceTabId', spaceTab.spaceTabId.toString())
          .replaceAll(':spaceId', spaceTab.space.spaceId?.toString())
          .replaceAll(':projectId', projectId.toString()),

        hash: `ppi=${projectId}`,
      });
    },
    [projectId, router],
  );

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      {isResponsive && currentSpace ? (
        <>
          <StyledBackWrapperInCpSpaces>
            <BackIconWrapper onClick={() => onSpaceClick(undefined)}>
              <ArrowLeft20 />
            </BackIconWrapper>
            <CpWidgetHeader.Title autoAdjustColor>
              {currentSpace?.spaceName}
            </CpWidgetHeader.Title>
          </StyledBackWrapperInCpSpaces>
        </>
      ) : (
        <CpWidgetHeader>
          <CpWidgetHeader.Title autoAdjustColor>
            {data.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description autoAdjustColor>
            {data.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
      )}
      {!isResponsive || currentSpace ? (
        <SearchInput
          prefix={<Search16 />}
          value={search}
          placeholder={translations('search')}
          onChange={(e) => setSearch(e.target.value)}
          suffix={
            search ? <Close20 onClick={() => setSearch('')} /> : undefined
          }
        />
      ) : null}
      {isResponsive ? (
        <CpAccountSpacesForMobile
          currentSpaceTabs={currentSpaceTabs}
          currentSpace={currentSpace}
          data={data}
          mode={mode}
          onSpaceClick={onSpaceClick}
          search={search}
          setMode={setMode}
          onSpaceTabClick={onSpaceTabClick}
        />
      ) : (
        <CpAccountSpacesForLaptop
          currentSpaceTabs={currentSpaceTabs}
          currentSpace={currentSpace}
          data={data}
          mode={mode}
          onSpaceClick={onSpaceClick}
          search={search}
          setMode={setMode}
          onSpaceTabClick={onSpaceTabClick}
        />
      )}
    </CpSectionWrapper>
  );
};
